/**
 * H13 four biome cities in one table (trade, sea port, desert, snow peak). Each is the H7 town shell
 * (townGrey.ts: safe plaza, warehouse + trainhall, monster pocket SE, exit gap north back to plains) with a cheap
 * ground swap per biome and its own pocket monster. TODO-polish: real layouts, biome props, a second exit.
 */
import type { LayoutSpec, LayoutOp } from '../../layout';
import type { DoorLink } from '../interiors';
import type { SafeZoneDef } from '../safeZones';
import type { ZoneDef } from '../zones';
import { TOWN_PLAZA, townLayout, townZone } from './townGrey';

interface CityRow {
  readonly id: string;
  readonly name: string;
  readonly plaza: string;
  readonly monster: string;
  readonly tint: readonly [number, number, number];
  /** Ground painted under the town shell (earlier ops lose to later ones). */
  readonly ground: readonly LayoutOp[];
}

const R = (x0: number, x1: number, y0: number, y1: number) => ({ rect: [x0, x1, y0, y1] }) as const;

export const CITY_TABLE: readonly CityRow[] = [
  {
    id: 'trade', name: 'เมืองตลาดสายธาร', plaza: 'ลานตลาดสายธาร', monster: 'bandit', tint: [1.04, 1.0, 0.94],
    ground: [{ name: 'market street', shape: R(3, 93, 56, 60), kind: 'pave', level: 0 }],
  },
  {
    id: 'sea', name: 'ท่าคลื่นเงิน', plaza: 'ลานท่าคลื่นเงิน', monster: 'masked', tint: [0.94, 1.02, 1.08],
    ground: [
      { name: 'shore sand', shape: R(3, 20, 3, 93), kind: 'sand', level: 0 },
      { name: 'sea', shape: R(3, 11, 3, 93), kind: 'sea', level: 0 },
      { name: 'pier', shape: R(8, 20, 54, 58), kind: 'deck', level: 0 },
    ],
  },
  {
    id: 'desert', name: 'เมืองทรายแดง', plaza: 'ลานทรายแดง', monster: 'scorpion', tint: [1.1, 1.0, 0.86],
    ground: [{ name: 'dunes', shape: R(3, 93, 3, 93), kind: 'sand', level: 0 }],
  },
  {
    id: 'snow', name: 'ตำหนักยอดน้ำแข็ง', plaza: 'ลานยอดน้ำแข็ง', monster: 'snowwolf', tint: [0.92, 1.02, 1.14],
    ground: [{ name: 'snowfield', shape: R(3, 93, 3, 93), kind: 'snow', level: 0 }],
  },
];

const zid = (id: string): string => `city-${id}`;

export const CITY_LAYOUTS: Readonly<Record<string, LayoutSpec>> = Object.fromEntries(
  CITY_TABLE.map((r, i) => {
    const base = townLayout(zid(r.id), 900 + i);
    return [zid(r.id), { ...base, ops: [...r.ground, ...base.ops] } as LayoutSpec];
  }),
);
export const CITY_ZONES: readonly ZoneDef[] = CITY_TABLE.map((r, i) => ({
  ...townZone(zid(r.id), r.name, 900 + i, r.tint),
  spawns: [{ monster: r.monster, region: 'wolves', n: 6 }],
}));
export const CITY_SAFE_ZONES: readonly SafeZoneDef[] = CITY_TABLE.map((r) => ({
  zone: zid(r.id) as SafeZoneDef['zone'],
  id: `${zid(r.id)}-plaza`,
  name: r.plaza,
  circle: { at: [TOWN_PLAZA.at[0] + 0.5, TOWN_PLAZA.at[1] + 0.5], r: TOWN_PLAZA.r },
}));
/** Warehouse (keeper) and training hall (teacher) reuse the plains rooms, as H7 did. */
export const CITY_DOOR_LINKS: readonly DoorLink[] = CITY_TABLE.flatMap((r) => [
  { zone: zid(r.id) as DoorLink['zone'], building: `${zid(r.id)}-warehouse`, interior: 'plains-warehouse' },
  { zone: zid(r.id) as DoorLink['zone'], building: `${zid(r.id)}-trainhall`, interior: 'plains-trainhall' },
]);
